// Smaller student pages: certificates, verification, account, not-found, setup notice.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { listMyCertificates, updateProfile, verifyCertificate } from '../lib/api';
import { friendlyError, supabaseSettings } from '../lib/supabase';
import type { Certificate } from '../lib/types';
import { Badge, Button, Callout, Lockup, SectionTitle, Spinner, TextField } from '../components/ui';

const DISCLAIMER = 'Professional development hours are awarded by BlueLine Environmental Institute based on the course’s instructional time. A certificate documents completion of an educational course. It does not represent accreditation, licensure, certification or endorsement by any federal, state or local agency or professional organization.';

function Band({ overline, title, subtitle }: { overline: string; title: string; subtitle?: string }) {
  return (
    <section className="title-band">
      <div className="container title-band-inner"><div><SectionTitle overline={overline} subtitle={subtitle}>{title}</SectionTitle></div></div>
    </section>
  );
}

export function Certificates() {
  const { user } = useAuth();
  const [certs, setCerts] = useState<Certificate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (user) listMyCertificates(user.id).then(setCerts).catch((e) => setError(friendlyError(e))); }, [user]);
  return (
    <>
      <Band overline="Certificates" title="Your certificates" subtitle="Download your certificates and share their verification links." />
      <div className="container page">
        {error && <Callout variant="error">{error}</Callout>}
        {!certs && !error && <Spinner />}
        {certs && (certs.length === 0 ? (
          <div className="empty">No certificates yet. Complete a course to earn one.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Course</th><th className="num">PDH</th><th>Completed</th><th>Certificate ID</th><th>Actions</th></tr></thead>
              <tbody>
                {certs.map((c) => (
                  <tr key={c.id}>
                    <td>{c.course_title}</td>
                    <td className="num">{Number(c.pdh)}</td>
                    <td>{new Date(c.completed_on + 'T12:00:00').toLocaleDateString('en-US', { dateStyle: 'medium' })}</td>
                    <td><code>{c.public_id}</code></td>
                    <td className="row" style={{ gap: 12 }}>
                      {c.pdf_path ? <span className="small muted">PDF download arrives with certificate generation</span> : <Badge>PDF pending</Badge>}
                      <Link to={`/verify/${c.public_id}`}>Verification page</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        <p className="small muted" style={{ margin: 0 }}>{DISCLAIMER}</p>
      </div>
    </>
  );
}

type Verified = Awaited<ReturnType<typeof verifyCertificate>>;
export function VerifyCertificate() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(publicId ?? '');
  const [result, setResult] = useState<Verified | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!publicId) { setResult(undefined); return; }
    setResult(undefined); setError(null);
    verifyCertificate(publicId).then(setResult).catch((e) => setError(friendlyError(e)));
  }, [publicId]);

  function submit(e: FormEvent) { e.preventDefault(); if (input.trim()) navigate(`/verify/${encodeURIComponent(input.trim())}`); }
  const fmt = (d: string) => new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('en-US', { dateStyle: 'long' });

  return (
    <>
      <Band overline="Certificate verification" title="Verify a certificate" subtitle="Enter the ID printed at the bottom of a BlueLine certificate." />
      <div className="container page" style={{ maxWidth: 760 }}>
        <form className="row" onSubmit={submit} style={{ alignItems: 'flex-end' }}>
          <TextField label="Certificate ID" value={input} onChange={(e) => setInput(e.target.value)} style={{ textTransform: 'uppercase' }} />
          <Button type="submit" disabled={!input.trim()}>Verify</Button>
        </form>
        {error && <Callout variant="error">{error}</Callout>}
        {publicId && result === undefined && !error && <Spinner label="Checking…" />}
        {publicId && result === null && (
          <Callout variant="error">We couldn’t find a certificate with that ID. Check the ID printed at the bottom of the certificate and try again.</Callout>
        )}
        {result && (result.revoked_at ? (
          <Callout variant="error" title="This certificate is no longer valid.">Certificate ID {publicId?.toUpperCase()} was revoked on {fmt(result.revoked_at)}.</Callout>
        ) : (
          <section className="box stack" style={{ borderColor: 'var(--accent)', background: 'var(--tint-green)' }}>
            <Badge tone="success">Verified certificate</Badge>
            <p style={{ margin: 0, fontSize: 18, lineHeight: '28px' }}>
              <strong>{result.recipient_name}</strong> completed <strong>{result.course_title}</strong> with BlueLine Environmental Institute on {fmt(result.completed_on)} and was awarded {Number(result.pdh)} professional development hours.
            </p>
            <p className="small muted" style={{ margin: 0 }}>Certificate ID {publicId?.toUpperCase()} · Issued {fmt(result.issued_at)}</p>
          </section>
        ))}
        <p className="small muted" style={{ margin: 0 }}>{DISCLAIMER}</p>
      </div>
    </>
  );
}

export function Account() {
  const { user, profile, refreshProfile } = useAuth();
  const [form, setForm] = useState({ full_name: '', certificate_name: '', job_title: '', organization: '', email_reminders: true });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (profile) setForm({ full_name: profile.full_name, certificate_name: profile.certificate_name ?? '', job_title: profile.job_title ?? '', organization: profile.organization ?? '', email_reminders: profile.email_reminders });
  }, [profile]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      await updateProfile(user.id, {
        full_name: form.full_name.trim(),
        certificate_name: form.certificate_name.trim() || null,
        job_title: form.job_title.trim() || null,
        organization: form.organization.trim() || null,
        email_reminders: form.email_reminders,
      });
      await refreshProfile();
      setSaved(true);
    } catch (err) { setError(friendlyError(err)); }
    setBusy(false);
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <>
      <Band overline="Account" title="Account settings" />
      <div className="container page" style={{ maxWidth: 760 }}>
        <form className="stack" onSubmit={submit}>
          {error && <Callout variant="error">{error}</Callout>}
          {saved && <Callout variant="note" role="status">Saved.</Callout>}
          <div className="form-grid">
            <TextField label="Full name" required value={form.full_name} onChange={set('full_name')} />
            <TextField label="Name on certificates" help="Leave blank to use your full name." value={form.certificate_name} onChange={set('certificate_name')} />
            <TextField label="Job title" value={form.job_title} onChange={set('job_title')} />
            <TextField label="Organization" value={form.organization} onChange={set('organization')} />
          </div>
          <label className="check-row"><input type="checkbox" checked={form.email_reminders} onChange={set('email_reminders')} /> Send me reminders about unfinished courses</label>
          <p className="small muted" style={{ margin: 0 }}>Signed in as {user?.email}. Receipts, password and certificate emails are always sent.</p>
          <div><Button type="submit" busy={busy} disabled={!form.full_name.trim()}>Save changes</Button></div>
        </form>
        <p className="small" style={{ margin: 0 }}><Link to="/update-password">Change your password</Link></p>
      </div>
    </>
  );
}

export function NotFound({ message = 'We couldn’t find that page.' }: { message?: string }) {
  return (
    <div className="container page" style={{ maxWidth: 640 }}>
      <SectionTitle overline="Not found">Page not available</SectionTitle>
      <p>{message}</p>
      <p><Link to="/">Go to your dashboard</Link> or <Link to="/courses">browse courses</Link>.</p>
    </div>
  );
}

export function SetupNotice() {
  return (
    <div className="auth-wrap">
      <main className="auth-card">
        <Lockup size={44} />
        <h1>Almost ready</h1>
        <p style={{ margin: 0 }}>This deployment isn’t connected to its database yet. This build received:</p>
        <ul style={{ margin: 0 }}>
          <li><code>VITE_SUPABASE_URL</code>: <strong>{supabaseSettings.url ? 'set' : 'missing'}</strong></li>
          <li><code>VITE_SUPABASE_PUBLISHABLE_KEY</code>: <strong>{supabaseSettings.key ? 'set' : 'missing'}</strong></li>
        </ul>
        <p style={{ margin: 0 }}>Add the missing values in Cloudflare Pages → Settings → Variables and Secrets for this environment (Preview for the staging branch), then retry the deployment. Values are read when the site is built.</p>
      </main>
    </div>
  );
}
